import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function isStaff(supabase: any, userId: string) {
  const [a, s, r] = await Promise.all([
    supabase.rpc("has_role", { _user_id: userId, _role: "admin" }),
    supabase.rpc("has_role", { _user_id: userId, _role: "super_admin" }),
    supabase.rpc("has_role", { _user_id: userId, _role: "sales_rep" }),
  ]);
  return Boolean(a.data) || Boolean(s.data) || Boolean(r.data);
}


const MAX_DURATION_MS = 30 * 24 * 3600_000;
const MAX_FUTURE_MS = 3 * 365 * 24 * 3600_000;
const isoDate = z
  .string()
  .min(1)
  .refine((v) => !Number.isNaN(Date.parse(v)), "Invalid date/time");

const eventInput = z.object({
  title: z.string().min(2).max(160),
  description: z.string().max(4000).optional().nullable(),
  starts_at: isoDate,
  ends_at: isoDate.optional().nullable().or(z.literal("")),
  location: z.string().max(240).optional().nullable(),
  ticket_url: z.string().url().max(500).optional().nullable().or(z.literal("")),
  image_url: z.string().max(1000).optional().nullable(),
  image_urls: z.array(z.string().max(1000)).max(10).optional(),
});

/** Rejects past start times, inverted ranges and absurd durations. */
function assertValidSchedule(startsAt?: string | null, endsAt?: string | null, existingStart?: string | null) {
  const startRaw = startsAt ?? existingStart ?? null;
  if (!startRaw) return;
  const start = Date.parse(startRaw);
  if (Number.isNaN(start)) throw new Error("Start date/time is not valid.");
  // Only enforce "not in the past" when the start time is actually being set/changed.
  if (startsAt) {
    if (start < Date.now() - 5 * 60_000) throw new Error("Start time is in the past — pick a future date and time.");
    if (start > Date.now() + MAX_FUTURE_MS) throw new Error("Start time is too far in the future (max 3 years).");
  }
  if (endsAt) {
    const end = Date.parse(endsAt);
    if (Number.isNaN(end)) throw new Error("End date/time is not valid.");
    if (end <= start) throw new Error("End time must be after the start time.");
    if (end - start > MAX_DURATION_MS) throw new Error("An event can run for at most 30 days.");
  }
}


// --- partner: list ------------------------------------------------------------
export const listPartnerEvents = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ clientId: z.string().uuid() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    // RLS decides which rows this partner may see; admin_note is not publicly
    // granted, so re-read the visible rows with the privileged client.
    const { data: visible, error } = await context.supabase
      .from("partner_events")
      .select("id")
      .eq("client_id", data.clientId);
    if (error) throw error;
    const ids = (visible ?? []).map((r) => r.id);
    if (ids.length === 0) return [];
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error: readErr } = await supabaseAdmin
      .from("partner_events")
      .select("*")
      .in("id", ids)
      .order("starts_at", { ascending: false });
    if (readErr) throw readErr;
    return rows ?? [];
  });


// --- partner: create ----------------------------------------------------------
export const createPartnerEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ clientId: z.string().uuid(), event: eventInput, submit: z.boolean().optional() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    assertValidSchedule(data.event.starts_at, data.event.ends_at);
    const { data: row, error } = await context.supabase

      .from("partner_events")
      .insert({
        client_id: data.clientId,
        created_by: context.userId,
        title: data.event.title,
        description: data.event.description || null,
        starts_at: data.event.starts_at,
        ends_at: data.event.ends_at || null,
        location: data.event.location || null,
        ticket_url: data.event.ticket_url || null,
        image_url: data.event.image_url || null,
        image_urls: data.event.image_urls ?? [],
        status: data.submit ? "submitted" : "draft",
      })
      .select("*")
      .single();
    if (error) {
      const { isAccessError, diagnoseEventAccess } = await import("@/lib/partner-events-access.server");
      if (isAccessError(error)) {
        const d = await diagnoseEventAccess(context.supabase, context.userId, data.clientId);
        throw new Error(d.message);
      }
      throw error;
    }
    if (data.submit) {
      try {
        const { sendPartnerEventStatusEmail } = await import("@/lib/partner-event-email.server");
        await sendPartnerEventStatusEmail({ event: row, kind: "submitted", createdBy: context.userId });
      } catch (e) {
        console.error("[partner-events] submitted email failed:", e);
      }
    }
    return row;
  });

// --- partner: update ----------------------------------------------------------
export const updatePartnerEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid(), event: eventInput.partial(), submit: z.boolean().optional() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    if (data.event.starts_at || data.event.ends_at) {
      const { data: existing } = await context.supabase
        .from("partner_events")
        .select("starts_at")
        .eq("id", data.id)
        .maybeSingle();
      assertValidSchedule(data.event.starts_at, data.event.ends_at, existing?.starts_at ?? null);
    }
    type Patch = Database["public"]["Tables"]["partner_events"]["Update"];
    const patch: Patch = { ...(data.event as Patch) };

    (Object.keys(patch) as Array<keyof Patch>).forEach((k) => {
      if ((patch as Record<string, unknown>)[k as string] === "") (patch as Record<string, unknown>)[k as string] = null;
    });
    if (data.submit) patch.status = "submitted";
    const { data: updated, error } = await context.supabase
      .from("partner_events")
      .update(patch)
      .eq("id", data.id)
      .select("id")
      .single();
    if (error) {
      const { isAccessError, diagnoseEventAccess } = await import("@/lib/partner-events-access.server");
      if (isAccessError(error)) throw new Error((await diagnoseEventAccess(context.supabase, context.userId)).message);
      throw error;
    }
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error: readErr } = await supabaseAdmin
      .from("partner_events")
      .select("*")
      .eq("id", updated.id)
      .single();
    if (readErr) throw readErr;

    if (data.submit) {
      try {
        const { sendPartnerEventStatusEmail } = await import("@/lib/partner-event-email.server");
        await sendPartnerEventStatusEmail({ event: row, kind: "submitted", createdBy: row.created_by ?? context.userId });
      } catch (e) {
        console.error("[partner-events] submitted email failed:", e);
      }
    }
    return row;
  });


// --- partner: delete ----------------------------------------------------------
export const deletePartnerEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("partner_events")
      .delete()
      .eq("id", data.id);
    if (error) {
      const { isAccessError, diagnoseEventAccess } = await import("@/lib/partner-events-access.server");
      if (isAccessError(error)) throw new Error((await diagnoseEventAccess(context.supabase, context.userId)).message);
      throw error;
    }
    return { ok: true };
  });

// --- partner: upload image ---------------------------------------------------
export const uploadPartnerEventImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({
      clientId: z.string().uuid(),
      filename: z.string().min(1).max(200),
      contentType: z.string().min(3).max(80),
      base64: z.string().min(10),
    }).parse(data),
  )
  .handler(async ({ data, context }) => {
    // Verify caller has partner access to this client OR is staff
    const [{ data: link }, staff] = await Promise.all([
      context.supabase.from("partner_users").select("client_id").eq("user_id", context.userId).eq("client_id", data.clientId).maybeSingle(),
      isStaff(context.supabase, context.userId),
    ]);
    if (!link && !staff) {
      const { diagnoseEventAccess } = await import("@/lib/partner-events-access.server");
      throw new Error((await diagnoseEventAccess(context.supabase, context.userId, data.clientId)).message);
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const safe = data.filename.replace(/[^a-zA-Z0-9._-]/g, "_").slice(-80);
    const path = `partner-events/${data.clientId}/${Date.now()}_${safe}`;
    const bytes = Uint8Array.from(atob(data.base64), (c) => c.charCodeAt(0));
    const up = await supabaseAdmin.storage.from("content-images").upload(path, bytes, {
      contentType: data.contentType,
      upsert: false,
    });
    if (up.error) throw up.error;
    const { data: pub } = supabaseAdmin.storage.from("content-images").getPublicUrl(path);
    return { url: pub.publicUrl, path };
  });

// --- admin: list all ---------------------------------------------------------
export const listAllPartnerEvents = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ status: z.string().optional() }).parse(data ?? {}))
  .handler(async ({ data, context }) => {
    if (!(await isStaff(context.supabase, context.userId))) throw new Error("Forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin
      .from("partner_events")
      .select("*, crm_clients ( id, name, company_name )")
      .order("starts_at", { ascending: false });
    if (data.status) q = q.eq("status", data.status);
    const { data: rows, error } = await q;
    if (error) throw error;
    return rows ?? [];

  });

// --- admin: review (approve / reject / archive) ------------------------------
export const reviewPartnerEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({
      id: z.string().uuid(),
      action: z.enum(["approve", "reject", "archive"]),
      admin_note: z.string().max(2000).optional(),
    }).parse(data),
  )
  .handler(async ({ data, context }) => {
    if (!(await isStaff(context.supabase, context.userId))) throw new Error("Forbidden");
    type Patch = Database["public"]["Tables"]["partner_events"]["Update"];
    const patch: Patch = { admin_note: data.admin_note ?? null };
    if (data.action === "approve") { patch.status = "approved"; patch.approved_at = new Date().toISOString(); patch.approved_by = context.userId; }
    if (data.action === "reject") { patch.status = "rejected"; }
    if (data.action === "archive") { patch.status = "archived"; }
    const { supabaseAdmin: admin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await admin
      .from("partner_events").update(patch).eq("id", data.id).select("*").single();

    if (error) throw error;



    // Mirror into the public events calendar so partner events show on /happening.
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const slug = `partner-${String(row.id).slice(0, 8)}-${String(row.title)
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60)}`;
      if (data.action === "approve") {
        const payload = {
          source_name: "Partner",
          source_url: row.ticket_url || "https://whatfinland.com/venue-events",
          title: row.title,
          slug,
          short_description: (row.description ?? "").slice(0, 300),
          full_description: row.description ?? "",
          venue: row.location ?? null,
          start_date: row.starts_at,
          end_date: row.ends_at ?? null,
          event_image: row.image_url ?? null,
          ticket_url: row.ticket_url ?? null,
          approved: true,
          updated_at: new Date().toISOString(),
          raw_payload: { partner_event_id: row.id },
        };
        const { data: existing } = await supabaseAdmin
          .from("imported_events").select("id").eq("slug", slug).maybeSingle();
        if (existing) {
          await supabaseAdmin.from("imported_events").update(payload).eq("id", existing.id);
        } else {
          await supabaseAdmin.from("imported_events").insert(payload);
        }
      } else {
        await supabaseAdmin.from("imported_events").update({ approved: false }).eq("slug", slug);
      }
    } catch {
      /* mirroring is best-effort */
    }

    // Notify the partner by email on approve / reject (best-effort).
    if (data.action === "approve" || data.action === "reject") {
      try {
        const { sendPartnerEventStatusEmail } = await import("@/lib/partner-event-email.server");
        await sendPartnerEventStatusEmail({
          event: row,
          kind: data.action === "approve" ? "approved" : "rejected",
          createdBy: row.created_by ?? null,
        });
      } catch (e) {
        console.error("[partner-events] review email failed:", e);
      }
    }
    return row;
  });

// --- public: list approved upcoming events -----------------------------------
// partner_events is not readable by anon (it holds internal admin notes), so the
// public list is served server-side with an explicit safe-column projection.
export const listPublicPartnerEvents = createServerFn({ method: "GET" })
  .handler(async () => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const nowIso = new Date().toISOString();
    const { data, error } = await supabaseAdmin
      .from("partner_events")
      .select("id, title, description, starts_at, ends_at, location, ticket_url, image_url, client_id")
      .eq("status", "approved")
      .or(`ends_at.gte.${nowIso},and(ends_at.is.null,starts_at.gte.${nowIso})`)
      .order("starts_at", { ascending: true })
      .limit(100);
    if (error) return { items: [] as NonNullable<typeof data> };
    return { items: data ?? [] };

  });


// --- partner: can I create events? -------------------------------------------
export const getPartnerEventAccess = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ clientId: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const [{ data: link }, staff] = await Promise.all([
      context.supabase
        .from("partner_users")
        .select("client_id")
        .eq("user_id", context.userId)
        .eq("client_id", data.clientId)
        .maybeSingle(),
      isStaff(context.supabase, context.userId),
    ]);
    if (link || staff) return { allowed: true as const, reason: "ok" as const, message: "" };
    const { diagnoseEventAccess } = await import("@/lib/partner-events-access.server");
    const d = await diagnoseEventAccess(context.supabase, context.userId, data.clientId);
    return { allowed: false as const, reason: d.reason, message: d.message };
  });
